import type { ProductLineItem } from '@weft/services';
import { validatePerUnitPricing } from '@weft/services';
import type { TierUsageData, Currency, LineItemProductVariant } from '@weft/types';
import { isNotNullOrUndefined, validateNotEmptyString } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import { Price } from 'apps/app/src/components/design/Price';
import type { ColumnDefinition } from 'apps/app/src/components/design/table/table-components/table-body/table-body-components/table-row/table-row-components/table-cell/table-cell-types/column-definition';
import { getCurrencySymbol } from 'apps/app/src/services/currency-symbol-mapper';
import classNames from 'classnames';
import { DiscountCell } from './dynamic-invoice-view-components/UsageLineItemBody/DiscountCell';

export type InvoicePerUnitTableRow = LineItemProductVariant & {
  usageDataMapKey: string;
};

export function buildInvoicePerUnitTableColumns(
  lineItem: ProductLineItem,
  tiersUsageMap: { [key: string]: Array<TierUsageData>; },
  currency: Currency,
  isDisabledEdit: boolean,
  onResetClick?: (lineItemId: string, productVariantId: string, tierIndex: number) => void,
): ColumnDefinition<InvoicePerUnitTableRow>[] { //@TODO update column definition
  return [
    {
      headerClassName: 'text-left w-fit',
      header: 'Product',
      renderCell: ({ meter }) => (
        <p className='text-left'>
          {meter?.name}
        </p>
      ),
    },
    {
      headerClassName: 'text-right w-fit',
      header: 'QTY',
      renderCell: ({ usageDataMapKey }) => {
        const firstTierPeriodUnits = tiersUsageMap[usageDataMapKey]?.[0]?.periodUnits;

        return (
          <div className="text-right">
            {formatNumber(isNotNullOrUndefined(firstTierPeriodUnits) ? firstTierPeriodUnits : 0)}
          </div>
        );
      },
    },
    {
      headerClassName: 'text-center w-fit',
      header: 'Price Per Unit',
      renderCell: ({ pricing }) => {
        validatePerUnitPricing(pricing);

        return (
          <div className="flex justify-center">
            <Price currency={currency}>
              {pricing.priceConfiguration.price}
            </Price>
          </div>
        );
      },
    },
    {
      header: 'New price',
      headerClassName: 'w-[160px]',
      renderCell: ({ usageDataMapKey, productVariantId }) => {
        validateNotEmptyString(productVariantId);

        return (
          <div className="px-6">
            <DiscountCell
              disabled={isDisabledEdit}
              discount={tiersUsageMap[usageDataMapKey]?.[0]?.discountConfiguration ?? undefined}
              productVariantId={productVariantId}
              lineItemId={lineItem.id}
              tierIndex={0}
              currency={currency}
            />
          </div>
        );
      },
    },
    {
      header: 'Discount',
      headerClassName: 'w-fit m-auto',
      renderCell: () => (
        <p>
          {isNotNullOrUndefined(lineItem.revisionedLineItem) ? `${getCurrencySymbol(currency)}${formatNumber(lineItem.price - lineItem.revisionedLineItem.price)}` : '--'}
        </p>
      ),
    },
    {
      headerClassName: 'text-right w-fit',
      header: 'Amount',
      renderCell: ({ usageDataMapKey, pricing }) => {
        validatePerUnitPricing(pricing);
        const firstTierPeriodUnits = tiersUsageMap[usageDataMapKey]?.[0]?.periodUnits;
        const amount = pricing.priceConfiguration.price * (firstTierPeriodUnits ?? 0); 

        return (
          <div className="flex justify-end">
            <Price currency={currency}>
              {lineItem.revisionedLineItem?.price ?? amount}
            </Price>
          </div>
        );
      }, 
    },
    {
      header: ' ',
      headerClassName: 'w-fit m-auto',
      renderCell: ({ productVariantId }) => {
        return (
          <button
            disabled={isDisabledEdit}
            className={classNames(
              'm-auto w-fit underline text-[#9EC3E4] opacity-0 pointer-events-none transition duration-200 ease-linear',
              { 'group-hover/row:opacity-100 pointer-events-auto': !!isNotNullOrUndefined(lineItem.revisionedLineItem) },
            )}
            onClick={() => isNotNullOrUndefined(productVariantId) && onResetClick?.(lineItem.id, productVariantId, 0)}
          >
            Reset
          </button>
        );
      },
    },
  ];
}